import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Screen from '@/karehero-app/explore/Screen';
import Chat, { ChatInput } from '@/karehero-app/explore/Chat';
import { DIARY } from '@/karehero-app/explore/tell-us-once/TellUsOnce.content';

// Section D — the ambient diary. Rough on purpose: the questions matter more
// than the frames, and sketching these further would imply answers we don't
// have. Every frame here is drawn to raise a fabrication-risk question.

// D1 — one sentence, three fields. The routed chips are the frame's argument
// and its warning at the same time: the further a single utterance reaches
// unprompted, the more useful it is and the more it can invent.
export const D1DiaryReach = () => {
  const c = DIARY.d1;
  return (
    <Screen footer={<ChatInput placeholder='Tell me anything…' />}>
      <Stack sx={{ pt: 1 }} spacing={2.5}>
        <Chat turns={c.turns} />

        <Paper variant='outlined'>
          <Stack sx={{ p: 1.75 }} spacing={1.25}>
            <Typography variant='caption' color='text.secondary'>
              {c.routedLead}
            </Typography>
            <Stack
              sx={{ flexWrap: 'wrap' }}
              direction='row'
              spacing={0.75}
              useFlexGap
            >
              {c.routed.map((r) => (
                <Chip key={r} size='small' label={r} color='cherry' />
              ))}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Screen>
  );
};
D1DiaryReach.displayName = 'D1DiaryReach';

// D2 — the diary as a correctable log, Granola-style: what you said kept
// verbatim above what we made of it. Nothing is promoted into a claim until
// the confirm below it is tapped, which is the whole trust mechanism.
export const D2DiaryReview = () => {
  const c = DIARY.d2;
  return (
    <Screen title={c.lead} back>
      <Stack sx={{ pb: 3 }} spacing={2}>
        {c.entries.map((e) => (
          <Paper key={e.date} variant='outlined'>
            <Stack sx={{ p: 2 }} spacing={1.5}>
              <Typography variant='caption' color='text.secondary'>
                {e.date}
              </Typography>

              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  bgcolor: 'custom.neutral.50',
                  borderLeft: 2,
                  borderColor: 'custom.neutral.300',
                  borderRadius: '0 8px 8px 0',
                }}
              >
                <Typography sx={{ fontStyle: 'italic' }} variant='body2'>
                  “{e.raw}”
                </Typography>
              </Box>

              <Stack spacing={0.75}>
                <Stack
                  sx={{ alignItems: 'center' }}
                  direction='row'
                  spacing={0.75}
                >
                  <Box sx={{ color: 'custom.cherry.500', display: 'flex' }}>
                    <Icon icon={'star' as never} fontSize={11} />
                  </Box>
                  <Typography variant='caption' color='text.secondary'>
                    What we made of it
                  </Typography>
                </Stack>
                <Typography variant='body2'>{e.madeOf}</Typography>
              </Stack>

              <Divider />

              <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
                <Button ariaLabel={c.confirm} size='sm'>
                  {c.confirm}
                </Button>
                <Button ariaLabel={c.edit} size='sm' variant='link'>
                  {c.edit}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Screen>
  );
};
D2DiaryReview.displayName = 'D2DiaryReview';

// D3 — the calendar as a passive source. The derived line at the bottom is
// what the frame is really asking about: could whole form sections be worked
// out rather than asked for?
export const D3Calendar = () => {
  const c = DIARY.d3;
  return (
    <Screen
      title={c.lead}
      back
      footer={
        <Button ariaLabel={c.cta} isFullWidth>
          {c.cta}
        </Button>
      }
    >
      <Stack spacing={2.5}>
        <Typography variant='body2' color='text.secondary'>
          {c.prompt}
        </Typography>

        <Stack divider={<Divider />}>
          {c.events.map((e) => (
            <Stack
              sx={{ alignItems: 'center', py: 1.5 }}
              key={e.title}
              direction='row'
              spacing={2}
            >
              <Typography
                sx={{ width: 34, flexShrink: 0 }}
                variant='overline'
                color='text.secondary'
              >
                {e.day}
              </Typography>
              <Typography sx={{ flex: 1, minWidth: 0 }} variant='body2'>
                {e.title}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {e.time}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1.5}>
          <Box sx={{ color: 'custom.cherry.500', display: 'flex', pt: '2px' }}>
            <Icon icon={'star' as never} fontSize={12} />
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {c.derived}
          </Typography>
        </Stack>
      </Stack>
    </Screen>
  );
};
D3Calendar.displayName = 'D3Calendar';
